import { parseAndVerifyContactDetails } from "@/moby/repo";
import { mui } from "@/mui";
import Dexie from "dexie";
import { useMemo, useState, useEffect, useRef, type FormEvent } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";
import { useMobyDB, useMobyNodeClient, useSession } from "./provider";
import { useLocationHash } from "@/hooks/useLocationHash";
import { MessagePacketType, WiredMessagePacket } from "@/moby/db";
import { binencode } from "@/lib/binary";

export function AddContactDialog() {
  const db = useMobyDB();
  const session = useSession();
  const nodeClient = useMobyNodeClient();
  const [, navigate] = useLocation();
  const hash = useLocationHash();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // MOUNT GATE: Defer dialog portal rendering until after the first paint.
  // During onboarding→chats transition, MobyChatProvider and FileTransferManagerWrapper
  // are initializing in the same render cycle. Rendering the Base UI Dialog portal
  // (focus-trap, backdrop, scroll-lock) synchronously in that cycle triggers error #185.
  // Flipping this flag via useEffect (fires after paint) lets the provider tree settle first.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  
  // Reset isSubmitting when hash changes (new QR code scanned)
  useEffect(() => {
    setIsSubmitting(false);
  }, [hash]);

  const contact = useMemo(() => {
    if (hash === "" || !hash.startsWith("#")) {
      return undefined;
    }

    const code = hash.slice(1);
    try {
      return parseAndVerifyContactDetails(code);
    } catch (err) {
      if (!(err instanceof Error)) {
        console.error('[ADD_CONTACT] Unexpected non-Error throw during contact parsing:', err);
        return undefined;
      }
      navigate(window.location.pathname, { replace: true });
      toast.warning(err.message);
      return undefined;
    }
  }, [hash]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (contact === undefined || isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);

    const data = new FormData(e.currentTarget);

    const addContactFormSchema = z.object({
      nickname: z.literal("").or(
        z
          .string()
          .trim()
          .min(1, "Name is required.")
          .max(24, "Nickname must be less than 24 characters.")
          .regex(
            /^[a-zA-Z0-9](?:[a-zA-Z0-9._]|(?:\s[a-zA-Z0-9]))*[a-zA-Z0-9]$/,
            "Name may only contain letters, numbers, spaces, and underscores.",
          ),
      ),
    });

    const form = addContactFormSchema.safeParse({
      nickname: data.get("nickname"),
    });

    if (!form.success) {
      for (const error of form.error.errors) {
        toast.error(error.message);
      }
      return;
    }

    try {
      await db.contacts.add({
        address: contact.address,
        nickname: form.data.nickname || undefined,
        card: {
          name: contact.preferredName,
          signature: contact.signature,
        },
        addedAt: new Date(),
        custom: {
          unseen: true,
        },
      });
      
      // CRITICAL: Wait for IndexedDB transaction to fully commit
      // This ensures the contact is persisted before dispatching UI update event
      await new Promise(resolve => setTimeout(resolve, 50));

      // Send peer-to-peer notification to the added contact
      if (session && nodeClient) {
        try {
          const notificationPacket = binencode(WiredMessagePacket, {
            type: MessagePacketType.CONTACT_ADDED_NOTIFICATION,
            adderAddress: session.address,
            adderNickname: form.data.nickname || undefined,
            adderPreferredName: session.name || "Unknown",
            timestamp: BigInt(Date.now()),
          });

          // Ensure peer connection exists - create if needed
          let peer = nodeClient.peers.get(contact.address);
          if (!peer) {
            console.log("Creating new peer connection for contact", {
              peerAddress: contact.address.slice(0, 16) + "..."
            });
            // This will initiate the WebRTC connection
            peer = nodeClient.getOrCreatePeer(contact.address);

            // Wait for connection to initialize  
            await new Promise(resolve => setTimeout(resolve, 1500));
          }

// Send the notification through the peer's chat channel
if (peer && peer.chat.ready) {
            console.log("Sending CONTACT_ADDED_NOTIFICATION to ready peer", {
              peerAddress: contact.address.slice(0, 16) + "...",
              adderName: session.name || "Unknown",
              timestamp: new Date().toISOString()
            });
            await peer.chat.send(notificationPacket);
          } else {
            console.log("Peer not ready for immediate notification, queuing for later delivery", {
              peerAddress: contact.address.slice(0, 16) + "...",
              peerExists: !!peer,
              chatReady: peer?.chat?.ready || false
            });
            
            // Queue the notification for delivery when peer becomes ready
            nodeClient.queueNotificationForPeer(contact.address, notificationPacket);
          }
        } catch (notificationErr) {
          console.error("Failed to send contact added notification:", notificationErr);
          // Don't show error to user as the contact was still added successfully
        }
      }
    } catch (err) {
      setIsSubmitting(false); // Reset on error so user can retry
      if (!(err instanceof Error)) {
        throw err;
      }
      if (err instanceof Dexie.ConstraintError) {
        toast.error(
          `Either you already have a contact named "${form.data.nickname || contact.preferredName}", or the contact you're trying to add is already in your contacts list.`,
        );
        return;
      }
      toast.error(err.message);
      return;
    }

    // 🔧 FIX: Dispatch custom event to trigger immediate UI update in contacts drawer
    // This ensures the badge and contact list update instantly without waiting for reactive query
    window.dispatchEvent(new CustomEvent('moby-contact-added', {
      detail: {
        contactAddress: contact.address,
        contactName: form.data.nickname || contact.preferredName,
        timestamp: Date.now()
      }
    }));
    
    toast.success(
      `Added "${form.data.nickname || contact.preferredName}" to your contacts list.`,
    );
    // Clear the hash instead of navigating to chat - let user decide when to start chatting
    navigate(window.location.pathname, { replace: true });
    
    // Keep isSubmitting true to prevent dialog from reopening during hash clear
    setTimeout(() => setIsSubmitting(false), 500);
  };

  // FIX B: If contact already exists, skip dialog and route to calendar RSVP
  const existingContactChecked = useRef(false);
  useEffect(() => {
    if (!contact || isSubmitting || existingContactChecked.current) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await db.contacts.get(contact.address);
        if (cancelled) return;
        if (existing) {
          existingContactChecked.current = true;
          console.log('[ADD_CONTACT_FIX_B] Contact already exists, skipping dialog', {
            address: contact.address.slice(0, 8),
            name: existing.card?.name || existing.nickname,
          });
          // Clear hash to prevent dialog from re-showing
          navigate(window.location.pathname, { replace: true });
          // Notify calendar system to check for pending invites from this contact
          window.dispatchEvent(new CustomEvent('calendar-check-pending-invites', {
            detail: { contactAddress: contact.address },
          }));
          toast.info(
            `"${existing.card?.name || existing.nickname || 'Contact'}" is already in your contacts. Checking for calendar invites...`,
          );
        }
      } catch { /* ignore lookup errors — fall through to normal dialog */ }
    })();
    return () => { cancelled = true; };
  }, [contact, isSubmitting, db, navigate]);
  // Reset the check flag when hash changes
  useEffect(() => { existingContactChecked.current = false; }, [hash]);

  // Don't show dialog if not yet mounted, no contact hash, or currently submitting
  if (!mounted || contact === undefined || isSubmitting) {
    return null;
  }

  return (
    <mui.Dialog.Root open={contact !== undefined && !isSubmitting} dismissible={false}>
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop />
        <mui.Dialog.Popup>
          <mui.Dialog.Title>Add Contact</mui.Dialog.Title>
          <mui.Dialog.Description className="mt-4 whitespace-pre-wrap">
            Would you like to add "{contact.preferredName}" to your contacts
            list?
          </mui.Dialog.Description>

          <form className="contents" onSubmit={onSubmit}>
            <div className="mt-6 mb-4">
              <div className="relative">
                <mui.Input
                  filled
                  type="text"
                  name="nickname"
                  placeholder={contact.preferredName}
                  className="rounded-t-extra-small"
                />
                <mui.FloatingLabel htmlFor="nickname">
                  Nickname
                </mui.FloatingLabel>
              </div>
              <mui.InputDescription>
                Leave blank to use the contact's preferred name.
              </mui.InputDescription>
            </div>

            <div className="">
              <div className="relative">
                <mui.Input
                  readOnly
                  value={contact.address}
                  className="text-on-surface-variant truncate"
                />
                <mui.FloatingLabel>Public Key</mui.FloatingLabel>
              </div>

              <div className="relative">
                <mui.Input
                  readOnly
                  value={contact.signature}
                  className="text-on-surface-variant truncate"
                />
                <mui.FloatingLabel>Verified Signature</mui.FloatingLabel>
              </div>
            </div>

            <div className="mt-6 mb-2 flex justify-end gap-2">
              <mui.Dialog.Close
                render={
                  <mui.Button
                    type="button"
                    className="text-label-lg text-primary"
                    onClick={() => {
                      navigate(window.location.pathname, { replace: true });
                    }}
                  >
                    Cancel
                  </mui.Button>
                }
              />

              <mui.Button type="submit" className="text-label-lg text-primary">
                OK
              </mui.Button>
            </div>
          </form>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}
